import { useCallback, useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { z } from "zod";
import { getCoreAxiosInstance } from "../../api/CoreAxiosInstance";
import type { MyPayEnvironment } from "../../generated/core-api";
import {
  COMMIT_CHUNK_SIZE,
  COMMIT_POLL_INITIAL_DELAY_MS,
  COMMIT_POLL_INTERVAL_MS,
  COMMIT_POLL_MAX_ATTEMPTS,
  COMMIT_POLL_MAX_TRANSIENT_ERRORS,
  COMMIT_POLL_TRANSIENT_BACKOFF_MS,
  PAY_COMPONENT_IMPORT_WORKFLOW,
} from "../config";
import {
  ImportValidationErrorSchema,
  UnmappedElementSchema,
  type ImportResult,
  type ImportValidationError,
  type MappingEntry,
} from "../types";
import {
  buildImportRows,
  findUnresolvedAmbiguousRows,
  type ComponentResolutionMap,
} from "../utils/buildImportRows";
import { aggregateChunkResults } from "../utils/commitChunks";
import { chunkCorrelationId, createRunId } from "../utils/commitCorrelation";
import {
  clearCommitProgress,
  dateColumnsStillUnset,
  importShapeKey,
  loadCommitProgressResumable,
  saveCommitProgress,
} from "../utils/commitProgress";

/** Arguments for a commit run. */
export interface CommitArgs {
  csvText: string;
  providerId?: string | null;
  componentColumn?: string | null;
  startDateColumn?: string | null;
  endDateColumn?: string | null;
  mypayEnvironment: MyPayEnvironment;
  /**
   * Confirmed column→parameter mappings, sent INLINE in each commit invocation.
   * The commit workflow uses no shared mapping store, so these must accompany
   * the rows (the API resolves each row against them + provider templates).
   */
  mappings: MappingEntry[];
  /**
   * Per-component-name ambiguity resolutions (D4). Carries the chosen
   * `companyComponentId` for ambiguous names so it lands on the wire row; when
   * an ambiguous name is left unresolved the commit is blocked with a
   * `PAYCOMP_COMPONENT_AMBIGUOUS` validation error rather than posted.
   */
  componentResolutions?: ComponentResolutionMap;
}

/** Per-chunk commit progress. */
export interface CommitProgress {
  chunk: number;
  totalChunks: number;
  committed: number;
  failed: number;
}

export interface WorkflowCommitConfig {
  agentId: string | undefined;
  serviceAccountId: string | undefined;
}

// ─── Runtime validation of the agent-invocation responses ────────────────────

const WorkflowRowErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
});

const WorkflowRowResultSchema = z.object({
  rowIndex: z.number(),
  employeeCode: z.string(),
  componentName: z.string(),
  status: z.enum(["created", "updated", "valid", "invalid", "failed"]),
  assignmentId: z.string().nullable().optional(),
  parameterValueIds: z.array(z.string()).nullable().optional(),
  // Tolerate the key being missing OR explicitly `null` (matching the
  // `.nullable()` neighbours above). `.default([])` alone rejects `null`, and
  // because commit chunks write server-side *before* this parse, a `null` here
  // would throw and reject the whole in-flight commit after rows had committed.
  errors: z
    .array(WorkflowRowErrorSchema)
    .nullish()
    .transform((v) => v ?? []),
});

const WorkflowResultSchema = z.object({
  status: z.string(),
  committed: z.number(),
  failed: z.number(),
  errors: z.array(ImportValidationErrorSchema),
  unmapped: z.array(UnmappedElementSchema),
  rows: z.array(WorkflowRowResultSchema),
});
type WorkflowResult = z.infer<typeof WorkflowResultSchema>;

const InvocationStartSchema = z.object({
  workflow_id: z.string(),
  run_id: z.string().optional(),
});

const InvocationStatusSchema = z.object({
  workflow_id: z.string(),
  run_id: z.string().optional(),
  status: z.enum(["RUNNING", "COMPLETED", "FAILED", "TERMINATED"]),
  result: WorkflowResultSchema.nullable().optional(),
  error: z.string().nullable().optional(),
});

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * A poll error we should ride out rather than fail on: an axios *network* error
 * (no response at all — connection reset, timeout, offline) or an HTTP 5xx.
 * These are the failure modes a healthy backend recovers from between polls, so
 * the poll loop retries them with backoff up to `COMMIT_POLL_MAX_TRANSIENT_ERRORS`
 * consecutive occurrences (D7). Everything else — a 4xx, or a response-schema
 * parse throw — is a hard failure surfaced immediately.
 */
function isTransientPollError(err: unknown): boolean {
  if (!isAxiosError(err)) return false;
  if (!err.response) return true; // no response => network / timeout
  return err.response.status >= 500;
}

/**
 * Render a per-row workflow error (`{code, message}`) as a single display
 * string, preserving BOTH the machine `code` (`PAYCOMP_*` / `XEM…`) and the
 * human `message` so the failed/invalid-rows table surfaces the code the PS
 * user needs to act on (e.g. `PAYCOMP_COMPONENT_AMBIGUOUS`), not just the prose.
 * Mirrors the `CODE: message` shape the pre-flight `invalid` errors already
 * arrive in. Falls back to the message alone if a code is ever absent.
 */
function formatRowError(e: { code: string; message: string }): string {
  return e.code ? `${e.code}: ${e.message}` : e.message;
}

/** Merge two error lists, de-duplicating the second against the first by row. */
function mergeErrorsByRow(
  primary: ImportValidationError[],
  secondary: ImportValidationError[],
): ImportValidationError[] {
  const seenRows = new Set(primary.map((e) => e.row));
  return [...primary, ...secondary.filter((e) => !seenRows.has(e.row))];
}

/**
 * Map a (validated) workflow result onto the wizard's ImportResult shape.
 *
 * The API's workflow-level `status` is a coarse `COMPLETED` / `FAILED`
 * (`PayComponentImportResult` extends `WorkflowResult`), so it can't map 1:1
 * onto `ImportStatus`. We combine it with the result shape: `status` is the
 * primary signal for whether the commit succeeded, and the row/error/unmapped
 * shape decides which screen to route to. Crucially, a FAILED workflow that
 * committed nothing must NOT be reported as `"committed"` (which renders the
 * success/results view) — earlier this fell through to `"committed"` whenever
 * the shape didn't match the two special cases.
 */
function toImportResult(wf: WorkflowResult): ImportResult {
  // 1. Unmapped elements block the commit → back to the mapping screen
  //    (regardless of the coarse workflow status).
  if (wf.unmapped.length > 0) {
    return {
      status: "needs_mapping",
      committed: 0,
      failed: 0,
      errors: [],
      unmapped: wf.unmapped,
      mappingsSaved: 0,
    };
  }

  // Per-row failures ("failed" writes and "invalid" validation rejects) become
  // error-table rows, merged with any workflow-level blocking pre-flight
  // errors. Per the API contract these two channels are mutually exclusive
  // (`rows` is empty when `errors` is populated), but merging de-duped by row
  // guarantees a populated `wf.errors` is never silently dropped.
  const failedRowErrors: ImportValidationError[] = wf.rows
    .filter((r) => r.status === "failed" || r.status === "invalid")
    .map((r) => ({
      row: r.rowIndex,
      employeeCode: r.employeeCode,
      componentCode: r.componentName,
      error: r.errors.map(formatRowError).join("; ") || "Commit failed",
    }));
  const errors = mergeErrorsByRow(failedRowErrors, wf.errors);

  const workflowFailed = wf.status.toLowerCase() === "failed";

  // 2. No rows were attempted → the batch was rejected pre-flight (or empty).
  //    Surface as "invalid" (the errors screen) when there's anything to show
  //    or the workflow failed.
  if (wf.rows.length === 0) {
    if (errors.length > 0 || workflowFailed) {
      return {
        status: "invalid",
        committed: 0,
        failed: wf.failed,
        errors,
        unmapped: [],
        mappingsSaved: 0,
      };
    }
    return {
      status: "committed",
      committed: wf.committed,
      failed: wf.failed,
      errors,
      unmapped: [],
      mappingsSaved: 0,
    };
  }

  // 3. Rows were attempted but the workflow FAILED with nothing committed —
  //    not a successful import. Route to the errors screen rather than the
  //    committed/results view that says "the import has been committed".
  if (workflowFailed && wf.committed === 0) {
    return {
      status: "invalid",
      committed: 0,
      failed: wf.failed,
      errors,
      unmapped: [],
      mappingsSaved: 0,
    };
  }

  // 4. Fully or partially committed → results screen (committed + failed
  //    counts + the per-row error table).
  return {
    status: "committed",
    committed: wf.committed,
    failed: wf.failed,
    errors,
    unmapped: [],
    mappingsSaved: 0,
  };
}

/** Shift a non-committed chunk result's error row numbers back to source rows. */
function offsetErrors(result: ImportResult, offset: number): ImportResult {
  if (offset === 0) return result;
  return {
    ...result,
    errors: result.errors.map((e) => ({ ...e, row: e.row + offset })),
  };
}

/**
 * The distinct code #892 returns for `rowMode=Create` against an assignment that
 * already exists. On a fresh, never-attempted chunk this is a genuine source
 * duplicate; on a chunk we already attempted before an interruption it is our
 * own earlier write replaying (D5a).
 */
const ASSIGNMENT_EXISTS_CODE = "PAYCOMP_ASSIGNMENT_EXISTS";

/**
 * D5a: re-credit the rows a *replayed* chunk reports as
 * `PAYCOMP_ASSIGNMENT_EXISTS` as already-committed rather than surfacing them as
 * hard errors.
 *
 * Only ever applied to the chunk that was in-flight when a prior run stopped
 * (the resume boundary — see the caller). On such a chunk an EXISTS row is this
 * import's own earlier write reappearing, so a row whose *only* error is
 * `PAYCOMP_ASSIGNMENT_EXISTS` is converted to a committed (`created`) row and the
 * committed/failed tallies adjusted. Rows carrying any other error are left
 * untouched (still a real failure). A never-attempted chunk is passed through
 * verbatim by the caller, so a genuine source duplicate stays an error.
 */
function creditReplayedAssignmentExists(wf: WorkflowResult): WorkflowResult {
  let recredited = 0;
  const rows = wf.rows.map((r) => {
    const onlyAssignmentExists =
      (r.status === "failed" || r.status === "invalid") &&
      r.errors.length > 0 &&
      r.errors.every((e) => e.code === ASSIGNMENT_EXISTS_CODE);
    if (!onlyAssignmentExists) return r;
    recredited += 1;
    return { ...r, status: "created" as const, errors: [] };
  });
  if (recredited === 0) return wf;
  return {
    ...wf,
    committed: wf.committed + recredited,
    failed: Math.max(0, wf.failed - recredited),
    rows,
  };
}

/**
 * Commit an import by invoking the `pay_component_import` Temporal workflow via
 * the agent-invocation surface, in row-chunks of at most `COMMIT_CHUNK_SIZE`,
 * polling each to completion and aggregating the per-chunk results. The API
 * caps a single invocation at 100 rows (`MAX_ROWS_PER_INVOCATION`) and rejects
 * anything larger with HTTP 400, so `COMMIT_CHUNK_SIZE` must stay <= 100.
 * Drop-in replacement for the old `useChunkedCommit` (same
 * `{ commit, reset, progress, isCommitting }` shape).
 */
export function useWorkflowCommit(
  companyCode: string,
  { agentId, serviceAccountId }: WorkflowCommitConfig,
) {
  const [progress, setProgress] = useState<CommitProgress | null>(null);
  const [isCommitting, setIsCommitting] = useState(false);

  // Guards against setState after the provider unmounts mid-commit (the poll
  // loop can run for minutes; leaving the wizard must not update a dead tree).
  const mountedRef = useRef(true);
  // Aborts the in-flight invocation/poll requests of the current commit run so
  // a run that outlives the wizard (unmount) or is superseded by a new commit
  // cannot race a live run or land results on a dead tree (D7).
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const reset = useCallback(() => setProgress(null), []);

  const commit = useCallback(
    async (args: CommitArgs): Promise<ImportResult> => {
      if (!agentId || !serviceAccountId) {
        throw new Error(
          "Onboarding agent is not configured. Set ONBOARDING_AGENT_ID and " +
            "ONBOARDING_SERVICE_ACCOUNT_ID for this environment.",
        );
      }

      // Supersede any still-running commit: abort its in-flight invocation/poll
      // so a stale run can't race this one or write state behind it (D7). Each
      // run gets its own controller; only the run whose controller is still the
      // active one may touch state (see `isActive`), and provider unmount aborts
      // it too (the effect cleanup above).
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const { signal } = controller;
      const isActive = () =>
        mountedRef.current && abortRef.current === controller;

      // Clear any prior run's progress so a new import doesn't briefly show the
      // previous run's batch count/percentage before the first chunk reports.
      setProgress(null);

      const allRows = buildImportRows(args.csvText, {
        companyCode,
        componentColumn: args.componentColumn,
        startDateColumn: args.startDateColumn,
        endDateColumn: args.endDateColumn,
        componentResolutions: args.componentResolutions,
      });

      // D4: block the commit before posting when an ambiguous component name has
      // no selected `companyComponentId`. Surface each as a blocking
      // `PAYCOMP_COMPONENT_AMBIGUOUS` row error (the server would reject them
      // anyway) rather than silently picking an instance.
      const unresolvedAmbiguous = findUnresolvedAmbiguousRows(
        allRows,
        args.componentResolutions ?? {},
      );
      if (unresolvedAmbiguous.length > 0) {
        return {
          status: "invalid",
          committed: 0,
          failed: unresolvedAmbiguous.length,
          errors: unresolvedAmbiguous.map((r) => ({
            row: r.rowIndex,
            employeeCode: r.employeeCode,
            componentCode: r.componentName,
            error:
              `PAYCOMP_COMPONENT_AMBIGUOUS: "${r.componentName}" matches more ` +
              `than one component for this company. Choose which instance to ` +
              `import on the mapping step.`,
          })),
          unmapped: [],
          mappingsSaved: 0,
        };
      }

      // Split into <= COMMIT_CHUNK_SIZE row invocations (API + Temporal limits).
      const chunks: (typeof allRows)[] = [];
      for (let i = 0; i < allRows.length; i += COMMIT_CHUNK_SIZE) {
        chunks.push(allRows.slice(i, i + COMMIT_CHUNK_SIZE));
      }
      if (chunks.length === 0) chunks.push([]);

      // Resumable commit (D5). Load any progress persisted by an interrupted run
      // of this same file for this same company. Each new attempt gets a fresh
      // run id so a re-import after success does not replay cached responses
      // (#212); resume reuses the persisted run id so in-flight chunks keep the
      // same per-chunk `correlation_id` and the API idempotency key (#892).
      const shapeKey = importShapeKey({
        componentColumn: args.componentColumn,
        startDateColumn: args.startDateColumn,
        endDateColumn: args.endDateColumn,
      });
      const saved = loadCommitProgressResumable(
        companyCode,
        args.mypayEnvironment,
        args.csvText,
        shapeKey,
        {
          allowUnshapedFallback: dateColumnsStillUnset({
            startDateColumn: args.startDateColumn,
            endDateColumn: args.endDateColumn,
          }),
        },
      );
      const runId = saved?.runId ?? createRunId();
      if (!saved) {
        saveCommitProgress(
          companyCode,
          args.mypayEnvironment,
          args.csvText,
          {
            runId,
            committedChunks: [],
            committed: 0,
            failed: 0,
          },
          shapeKey,
        );
      }
      const committedChunks = new Set<number>(saved?.committedChunks ?? []);
      const priorCommitted = saved?.committed ?? 0;
      const priorFailed = saved?.failed ?? 0;
      // The single chunk that was in-flight when the prior run stopped: the first
      // index not already committed. Only its replayed `PAYCOMP_ASSIGNMENT_EXISTS`
      // rows are credited as already-committed (D5a) — chunks beyond it were never
      // attempted, so an EXISTS there is a genuine source duplicate and stays an
      // error. -1 on a fresh run (no saved progress) disables all crediting.
      const boundaryChunk = saved
        ? chunks.findIndex((_, i) => !committedChunks.has(i))
        : -1;

      setIsCommitting(true);
      try {
        const instance = await getCoreAxiosInstance();
        const done: { result: ImportResult; offset: number }[] = [];

        for (let i = 0; i < chunks.length; i++) {
          const offset = i * COMMIT_CHUNK_SIZE;
          // Resume: a chunk already committed server-side is skipped — replaying
          // its `correlation_id` would return the cached response anyway, so we
          // save the round-trip and fold its persisted counts into the aggregate
          // below instead of re-committing it.
          if (committedChunks.has(i)) continue;

          // The API requires a 1-based `rowIndex` per wire row. Index within the
          // chunk so the per-chunk `offset` recovers the original file row number.
          const rows = chunks[i].map((row, idx) => ({
            ...row,
            rowIndex: idx + 1,
          }));
          const wfRaw = await runCommitChunk(
            instance,
            agentId,
            serviceAccountId,
            companyCode,
            args.providerId ?? null,
            args.mypayEnvironment,
            rows,
            args.mappings,
            chunkCorrelationId(runId, i),
            signal,
          );
          // D5a: on the resume boundary chunk, treat its own replayed
          // `PAYCOMP_ASSIGNMENT_EXISTS` rows as already-committed rather than
          // errors. Every other chunk is passed through verbatim.
          const wf =
            i === boundaryChunk ? creditReplayedAssignmentExists(wfRaw) : wfRaw;

          const mapped = toImportResult(wf);
          // A non-committed chunk (needs_mapping / invalid) stops the run.
          if (mapped.status !== "committed") {
            const failedChunk = offsetErrors(mapped, offset);
            // If earlier chunks (this run or a prior, resumed one) already
            // committed rows server-side, don't discard them: fold this chunk's
            // failures into a partial committed aggregate so the user sees what
            // actually landed rather than a misleading "0 committed". Only when
            // nothing has committed anywhere yet do we surface the raw
            // needs_mapping / invalid screen. Progress is left persisted so a
            // later retry can still resume the committed chunks.
            if (done.length === 0 && priorCommitted === 0) {
              return failedChunk;
            }
            const partial = aggregateChunkResults(done);
            return {
              ...partial,
              committed: partial.committed + priorCommitted,
              failed: partial.failed + priorFailed + mapped.failed,
              errors: [...partial.errors, ...failedChunk.errors],
              unmapped: failedChunk.unmapped,
            };
          }
          done.push({ result: mapped, offset });
          committedChunks.add(i);

          const agg = aggregateChunkResults(done);
          const committedNow = agg.committed + priorCommitted;
          const failedNow = agg.failed + priorFailed;
          // Persist per-chunk progress after each committed chunk so an
          // interruption can resume without re-committing (D5). Keyed by
          // (companyCode, file signature) via the helper.
          saveCommitProgress(
            companyCode,
            args.mypayEnvironment,
            args.csvText,
            {
              runId,
              committedChunks: [...committedChunks].sort((a, b) => a - b),
              committed: committedNow,
              failed: failedNow,
            },
            shapeKey,
          );
          if (isActive()) {
            setProgress({
              chunk: i + 1,
              totalChunks: chunks.length,
              committed: committedNow,
              failed: failedNow,
            });
          }
        }

        // Clean full completion: drop the resume record so a later identical
        // upload starts fresh rather than being mistaken for a resumable run.
        clearCommitProgress(
          companyCode,
          args.mypayEnvironment,
          args.csvText,
          shapeKey,
        );
        const finalAgg = aggregateChunkResults(done);
        return {
          ...finalAgg,
          committed: finalAgg.committed + priorCommitted,
          failed: finalAgg.failed + priorFailed,
        };
      } finally {
        if (isActive()) setIsCommitting(false);
      }
    },
    [companyCode, agentId, serviceAccountId],
  );

  return { commit, reset, progress, isCommitting };
}

/**
 * Start one workflow invocation for a chunk of rows and poll it to completion.
 * Returns the validated workflow result, or throws on a FAILED/TERMINATED
 * execution (or a fail-closed FAILED result carrying no rows/errors — e.g. the
 * server-authoritative scope rejection).
 */
async function runCommitChunk(
  instance: Awaited<ReturnType<typeof getCoreAxiosInstance>>,
  agentId: string,
  serviceAccountId: string,
  companyCode: string,
  providerId: string | null,
  mypayEnvironment: MyPayEnvironment,
  rows: unknown[],
  mappings: MappingEntry[],
  correlationId: string,
  signal: AbortSignal,
): Promise<WorkflowResult> {
  const startResp = await instance.post(
    `/api/v1/agents/${agentId}/invocations`,
    {
      service_account_id: serviceAccountId,
      input_payload: {
        workflow: PAY_COMPONENT_IMPORT_WORKFLOW,
        mode: "commit",
        // Deterministic per-chunk id (D5). The API threads this into its batch
        // `Idempotency-Key` (`PayComponentImportInput.correlation_id`), so an
        // identical replay returns the cached response instead of re-committing.
        correlation_id: correlationId,
        company_code: companyCode,
        provider_id: providerId,
        mypay_environment: mypayEnvironment,
        rows,
        mappings,
      },
    },
    { signal },
  );
  const { workflow_id: workflowId } = InvocationStartSchema.parse(startResp.data);

  // Give the freshly-started workflow a moment to register before the first
  // status GET, so the opening poll lands on real state rather than a miss (D7).
  await delay(COMMIT_POLL_INITIAL_DELAY_MS);

  // Ride out a bounded streak of transient poll failures (network / 5xx) so a
  // brief backend blip doesn't abort a commit whose rows may already be landing
  // server-side. Reset the streak on any successful poll (D7).
  let consecutiveTransientErrors = 0;
  let runningPolls = 0;

  while (runningPolls < COMMIT_POLL_MAX_ATTEMPTS) {
    let status: z.infer<typeof InvocationStatusSchema>;
    try {
      const statusResp = await instance.get(
        `/api/v1/agents/${agentId}/invocations/${workflowId}`,
        { signal },
      );
      status = InvocationStatusSchema.parse(statusResp.data);
    } catch (err) {
      // An aborted request (provider unmount, or superseded by a new commit)
      // stops the loop immediately. An aborted axios request presents like a
      // network error, so without this it would otherwise be retried as a
      // transient blip against the tolerance budget (D7).
      if (signal.aborted) throw err;
      // A 4xx or a schema-parse throw is a hard failure — surface it now.
      if (!isTransientPollError(err)) throw err;
      consecutiveTransientErrors += 1;
      if (consecutiveTransientErrors > COMMIT_POLL_MAX_TRANSIENT_ERRORS) {
        throw new Error(
          "Import status could not be retrieved after repeated network " +
            "errors. Check the results later.",
        );
      }
      // Back off progressively, then retry. A transient retry does NOT consume
      // the RUNNING poll-attempt budget (that guards a stuck-RUNNING workflow,
      // a distinct failure mode).
      await delay(COMMIT_POLL_TRANSIENT_BACKOFF_MS * consecutiveTransientErrors);
      continue;
    }
    // Any poll that returns and parses clears the transient streak.
    consecutiveTransientErrors = 0;

    if (status.status === "COMPLETED") {
      const wf = status.result;
      if (!wf) {
        throw new Error("Import completed without a result.");
      }
      // Fail-closed workflow result (e.g. scope rejection): FAILED with nothing
      // committed and no per-row/pre-flight detail. Do not render as success.
      if (
        wf.status.toLowerCase() === "failed" &&
        wf.rows.length === 0 &&
        wf.errors.length === 0 &&
        wf.unmapped.length === 0
      ) {
        throw new Error(
          "Import was rejected (insufficient scope or a precondition failed).",
        );
      }
      return wf;
    }
    if (status.status === "FAILED" || status.status === "TERMINATED") {
      throw new Error(
        status.error ?? `Import workflow ${status.status.toLowerCase()}.`,
      );
    }

    runningPolls += 1;
    await delay(COMMIT_POLL_INTERVAL_MS);
  }

  throw new Error("Import is taking longer than expected. Check the results later.");
}
